import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  ShieldCheck,
  Lock,
  Wrench,
  Quote,
  Phone,
} from "lucide-react";
import {
  hero,
  marquee,
  products,
  manifesto,
  whyUs,
  projects,
  testimonials,
  brand,
} from "../mock";
import { Reveal } from "../hooks/useReveal";

const iconMap = { Sparkles, ShieldCheck, Lock, Wrench };

function Hero() {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={hero.image}
          alt="Luxury modern home with premium windows"
          className="w-full h-[115%] object-cover"
          style={{ transform: `translateY(${offset * 0.25}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0d0f] via-[#0c0d0f]/55 to-[#0c0d0f]/70" />
      </div>

      <div className="af-container relative z-10 pb-24 pt-40 w-full">
        <div className="max-w-4xl">
          <p className="eyebrow mb-6 animate-[af-fade-up_0.8s_ease]">
            {hero.kicker}
          </p>
          <h1 className="font-display font-bold text-white leading-[0.95] text-6xl sm:text-7xl md:text-8xl lg:text-[7.5rem]">
            {hero.titleTop}
            <br />
            <span className="text-[var(--af-blue)]">{hero.titleBottom}</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/80 max-w-xl">
            {hero.subtitle}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/products"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0c0d0f] font-semibold rounded-full hover:bg-[var(--af-blue)] hover:text-white transition-all duration-300"
            >
              Explore Products
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-300"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = [...marquee, ...marquee];
  return (
    <div className="py-8 border-y border-[#1c1e23] bg-[#0a0b0d] overflow-hidden">
      <div className="af-marquee-track">
        {items.map((m, i) => (
          <span key={i} className="flex items-center">
            <span className="font-display text-2xl md:text-3xl text-white/70 px-8">
              {m}
            </span>
            <span className="text-[var(--af-green)] text-xl">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Products() {
  return (
    <section id="products" className="py-28 bg-[#0c0d0f]">
      <div className="af-container">
        <Reveal>
          <p className="eyebrow mb-4">01 — Products</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <h2 className="font-display font-bold text-white text-5xl md:text-6xl max-w-xl leading-tight">
              Designed for Every Space
            </h2>
            <p className="text-[var(--af-muted)] max-w-md">
              Four systems, one standard. Every window and door we manufacture is
              measured, made and installed to outlive the trends it was chosen for.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 90}>
              <article className="card-lift group relative rounded-2xl overflow-hidden border border-[#1e2026] bg-[#121317] h-full">
                <div className="img-zoom relative h-64">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121317] via-transparent to-transparent" />
                  <span className="absolute top-5 left-5 font-display text-2xl text-white/70">
                    {p.tag}
                  </span>
                </div>
                <div className="p-8">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {p.points.map((pt) => (
                      <span
                        key={pt}
                        className="text-xs px-3 py-1 rounded-full bg-white/5 text-white/70 border border-white/10"
                      >
                        {pt}
                      </span>
                    ))}
                  </div>
                  <h3 className="font-display font-semibold text-3xl text-white mb-3">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[var(--af-blue)] mb-4 tracking-wide">
                    {p.config}
                  </p>
                  <p className="text-[var(--af-muted)] leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12">
          <Link
            to="/products"
            className="group inline-flex items-center gap-2 text-white text-lg hover-underline"
          >
            Explore uPVC Systems & Finishes
            <ArrowUpRight
              size={20}
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="relative py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={manifesto.image}
          alt="Architectural facade at dusk"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0c0d0f]/85" />
      </div>
      <div className="af-container relative z-10">
        <Reveal>
          <p className="eyebrow mb-4">{manifesto.eyebrow}</p>
          <h2 className="font-display font-bold text-white text-4xl md:text-6xl max-w-4xl leading-tight mb-6">
            {manifesto.title}
          </h2>
          <p className="text-lg text-white/75 max-w-2xl mb-16">
            {manifesto.desc}
          </p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-10">
          {manifesto.items.map((item, i) => (
            <Reveal key={item.no} delay={i * 100}>
              <div className="border-t border-white/20 pt-6">
                <span className="font-display text-4xl text-[var(--af-blue)]">
                  {item.no}
                </span>
                <h3 className="font-display font-semibold text-2xl text-white mt-3 mb-3">
                  {item.title}
                </h3>
                <p className="text-[var(--af-muted)] leading-relaxed">
                  {item.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section className="py-28 bg-[#0a0b0d]">
      <div className="af-container">
        <Reveal>
          <p className="eyebrow mb-4">03 — Why August</p>
          <h2 className="font-display font-bold text-white text-5xl md:text-6xl mb-16">
            Why August Doors & Windows
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {whyUs.map((w, i) => {
            const Icon = iconMap[w.icon];
            return (
              <Reveal key={w.no} delay={i * 80}>
                <div className="card-lift group h-full p-8 rounded-2xl bg-[#121317] border border-[#1e2026] hover:border-[var(--af-blue)]/50">
                  <div className="w-12 h-12 rounded-xl bg-[var(--af-blue)]/12 flex items-center justify-center text-[var(--af-blue)] mb-6 group-hover:bg-[var(--af-blue)] group-hover:text-white transition-all">
                    <Icon size={22} />
                  </div>
                  <span className="font-display text-2xl text-white/30">
                    {w.no}
                  </span>
                  <h3 className="font-display font-semibold text-2xl text-white mt-1 mb-3">
                    {w.title}
                  </h3>
                  <p className="text-[var(--af-muted)] leading-relaxed">
                    {w.text}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Projects() {
  return (
    <section id="projects" className="py-28 bg-[#0c0d0f]">
      <div className="af-container">
        <Reveal>
          <p className="eyebrow mb-4">04 — Projects</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <h2 className="font-display font-bold text-white text-5xl md:text-6xl">
              Work That Speaks
            </h2>
            <p className="text-[var(--af-muted)] max-w-md">
              Residences, villas, towers and communities across South India — each
              one measured, manufactured and installed by our own team.
            </p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {projects.map((pr, i) => (
            <Reveal key={pr.title} delay={i * 80}>
              <div className="card-lift group relative rounded-2xl overflow-hidden border border-[#1e2026]">
                <div className="img-zoom aspect-[3/4]">
                  <img
                    src={pr.image}
                    alt={pr.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c0d0f] via-[#0c0d0f]/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-xs tracking-[0.2em] uppercase text-[var(--af-green)]">
                    {pr.tag}
                  </span>
                  <h3 className="font-display font-semibold text-2xl text-white mt-1">
                    {pr.title}
                  </h3>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="py-28 bg-[#0a0b0d]">
      <div className="af-container">
        <Reveal>
          <p className="eyebrow mb-4">05 — Kind Words</p>
          <h2 className="font-display font-bold text-white text-5xl md:text-6xl mb-16">
            Trusted Across Darbhanga
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <Reveal key={t.name} delay={i * 90}>
              <div className="h-full p-8 rounded-2xl bg-[#121317] border border-[#1e2026]">
                <Quote size={32} className="text-[var(--af-blue)] mb-5" />
                <p className="text-white/85 leading-relaxed mb-6">
                  “{t.quote}”
                </p>
                <div className="border-t border-[#1e2026] pt-4">
                  <p className="font-semibold text-white">{t.name}</p>
                  <p className="text-sm text-[var(--af-muted)]">{t.place}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="py-28 bg-[#0c0d0f]">
      <div className="af-container">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden border border-[#1e2026] p-12 md:p-20 bg-gradient-to-br from-[#14161b] to-[#0e0f13]">
            <div className="flex flex-wrap gap-3 mb-8 text-xs tracking-[0.28em] uppercase text-[var(--af-muted)]">
              <span>Manufacture</span>
              <span className="text-[var(--af-blue)]">·</span>
              <span>Supply</span>
              <span className="text-[var(--af-blue)]">·</span>
              <span>Install</span>
            </div>
            <h2 className="font-display font-bold text-white text-5xl md:text-7xl leading-[0.98] max-w-3xl">
              One company. Complete fenestration solutions.
            </h2>
            <p className="text-lg text-white/70 mt-6 max-w-xl">
              Let’s build something beautiful. Book a free consultation — we
              respond within 24 hours.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0c0d0f] font-semibold rounded-full hover:bg-[var(--af-blue)] hover:text-white transition-all"
              >
                Request My Free Quote
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
              <a
                href={brand.phoneHref}
                className="inline-flex items-center gap-2 px-8 py-4 border border-white/25 text-white font-semibold rounded-full hover:bg-white/10 transition-all"
              >
                <Phone size={17} /> {brand.phone}
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <Products />
      <Manifesto />
      <WhyUs />
      <Projects />
      <Testimonials />
      <CTA />
    </>
  );
}
